import { useMemo } from 'react'
import {
  apiReindexProofAssets,
  apiUpdateProofAsset,
  apiCreateTopicFromProofAsset,
} from '../studioApi.js'
import { useAuthority } from '../contexts/AuthorityContext'
import { useProof } from '../contexts/ProofContext'
import type {
  AuthorityInbox,
  AuthorityTopicCandidate,
  ProofAssetReindexResponse,
  ProofAssetUpdateResponse,
  ProofTopicCandidateResponse,
  StateSetter,
  StrategicAsset,
  StrategicAssetCoverage,
} from '../types'

export interface ProofHandlersApi {
  reindexAssets: () => Promise<ProofAssetReindexResponse>
  updateAsset: (
    id: string,
    updates: Partial<StrategicAsset>
  ) => Promise<ProofAssetUpdateResponse>
  createTopicFromAsset: (id: string) => Promise<ProofTopicCandidateResponse>
}

const DEFAULT_API: ProofHandlersApi = Object.freeze({
  reindexAssets: apiReindexProofAssets,
  updateAsset: apiUpdateProofAsset,
  createTopicFromAsset: apiCreateTopicFromProofAsset,
})

export interface ProofHandlersDeps {
  setAssets: StateSetter<StrategicAsset[]>
  setCoverage: StateSetter<StrategicAssetCoverage | null>
  applyAuthorityInbox: (payload?: AuthorityInbox | null) => void
  onToast?: (message: string) => void
  api?: ProofHandlersApi
}

export interface ProofHandlers {
  refreshProofAssets: () => Promise<ProofAssetReindexResponse>
  handleUpdateProofAsset: (
    id: string,
    updates: Partial<StrategicAsset>
  ) => Promise<StrategicAsset | null | undefined>
  handleCreateTopicFromProofAsset: (
    id: string
  ) => Promise<AuthorityTopicCandidate | undefined>
}

const noopToast = (_message: string): void => {}

// Pure factory for proof handlers (strategic assets + topic-from-asset).
//
// `handleCreateTopicFromProofAsset` crosses contexts: it touches the
// authority inbox AND proof assets. Composition lives here in the data
// layer rather than in App.jsx orchestration.
export function createProofHandlers(deps: ProofHandlersDeps): ProofHandlers {
  const {
    setAssets,
    setCoverage,
    applyAuthorityInbox,
    onToast = noopToast,
    api = DEFAULT_API,
  } = deps

  async function refreshProofAssets(): Promise<ProofAssetReindexResponse> {
    const data = await api.reindexAssets()
    setAssets(data?.assets || [])
    setCoverage(data?.coverage || null)
    return data
  }

  async function handleUpdateProofAsset(
    id: string,
    updates: Partial<StrategicAsset>
  ): Promise<StrategicAsset | null | undefined> {
    const data = await api.updateAsset(id, updates)
    setAssets((prev) =>
      [data?.asset, ...prev.filter((item) => item.id !== id)].filter(
        (item): item is StrategicAsset => Boolean(item)
      )
    )
    setCoverage(data?.coverage || null)
    return data?.asset
  }

  async function handleCreateTopicFromProofAsset(
    id: string
  ): Promise<AuthorityTopicCandidate | undefined> {
    const data = await api.createTopicFromAsset(id)
    if (data?.inbox) applyAuthorityInbox(data.inbox)
    if (data?.assets) setAssets(data.assets)
    if (data?.coverage) setCoverage(data.coverage)
    onToast('Proof-backed topic candidate created')
    return data?.candidate
  }

  return {
    refreshProofAssets,
    handleUpdateProofAsset,
    handleCreateTopicFromProofAsset,
  }
}

export interface UseProofHandlersOptions {
  onToast?: (message: string) => void
}

export function useProofHandlers(
  options: UseProofHandlersOptions = {}
): ProofHandlers {
  const { onToast } = options
  const { setAssets, setCoverage } = useProof()
  const { applyInbox } = useAuthority()

  return useMemo(
    () =>
      createProofHandlers({
        setAssets,
        setCoverage,
        applyAuthorityInbox: applyInbox,
        onToast,
      }),
    [setAssets, setCoverage, applyInbox, onToast]
  )
}

export const __test__ = { DEFAULT_API }
